import { fmtD } from './dates.ts';

/** Czynność przetwarzania (art. 30 ust. 1 RODO) albo kategoria przetwarzania w imieniu klienta (art. 30 ust. 2). */
export interface RodoActivity {
  name: string;
  role: 'controller' | 'processor';
  /** Dla roli podmiotu przetwarzającego: administrator, w którego imieniu przetwarzasz */
  onBehalfOf?: string;
  purposes: string;
  legalBasis: string;
  subjects: string;
  dataCategories: string;
  /** Dane szczególnych kategorii (art. 9) lub o wyrokach (art. 10) */
  special: boolean;
  source: string;
  retention: string;
  recipients: string;
  processorIds: string[];
  jointController: string;
  systems: string;
  /** Puste = ogólne środki bezpieczeństwa z nagłówka rejestru */
  security: string;
  transfers: string;
  transferSafeguards: string;
  dpia: string;
  active: boolean;
  order: number;
  templateKey?: string;
  updatedAt?: string;
}

export type DpaStatus = 'signed' | 'terms' | 'none' | 'unknown';

export interface RodoProcessor {
  name: string;
  service: string;
  contact?: string;
  country: string;
  dpa: DpaStatus;
  notes?: string;
}

export interface RodoMain {
  controllerName: string;
  address: string;
  nip: string;
  regon: string;
  email: string;
  phone: string;
  representative: string;
  dpo: string;
  security: string;
  processorMode: boolean;
  /** Data, na którą rejestr jest aktualny */
  asOf: string;
  reviewedAt?: string;
  wizardDone?: boolean;
  driveDocLink?: string;
  driveExportAt?: string;
}

export const DPA_LABEL: Record<DpaStatus, string> = {
  signed: 'umowa podpisana',
  terms: 'w regulaminie usługi',
  none: 'brak umowy',
  unknown: 'do sprawdzenia',
};

export const DEFAULT_SECURITY =
  'Dostęp do systemów tylko dla właściciela firmy, przez indywidualne konta z silnymi hasłami i weryfikacją dwuetapową. ' +
  'Połączenia szyfrowane (HTTPS), aktualne oprogramowanie, szyfrowane dyski urządzeń, kopie zapasowe. ' +
  'Umowy powierzenia z podmiotami przetwarzającymi. Procedura postępowania w razie naruszenia ochrony danych (zgłoszenie do UODO w ciągu 72 godzin).';

type Tpl = Omit<RodoActivity, 'active' | 'order' | 'processorIds' | 'role'> & {
  key: string;
  role?: RodoActivity['role'];
  /** Krótki opis w kreatorze */
  hint: string;
  /** Czy pasuje do działalności (sklep, faktury, B2B) – zaznaczone domyślnie */
  recommended: boolean;
  processors: string[];
};

/** Szablony czynności dla sklepu internetowego z dostawą i usługami B2B. Treść do weryfikacji i dopasowania. */
export const RODO_TEMPLATES: Tpl[] = [
  {
    key: 'sklep', recommended: true, hint: 'Zamówienia w sklepie: płatność, wysyłka, kontakt w sprawie zamówienia.',
    name: 'Sprzedaż w sklepie internetowym',
    purposes: 'Zawarcie i wykonanie umowy sprzedaży w sklepie internetowym: przyjęcie zamówienia, obsługa płatności, przygotowanie i wysyłka towaru, kontakt w sprawie zamówienia.',
    legalBasis: 'Art. 6 ust. 1 lit. b RODO (wykonanie umowy).',
    subjects: 'Klienci sklepu (konsumenci i przedsiębiorcy), odbiorcy przesyłek.',
    dataCategories: 'Imię i nazwisko, adres dostawy, adres e-mail, numer telefonu, treść i historia zamówień, informacje o płatności (status, kwota, bez danych kart), adres IP zapisany przy zamówieniu.',
    special: false, source: 'Bezpośrednio od klienta (formularz zamówienia).',
    retention: 'Przez czas realizacji umowy, a następnie do upływu terminu przedawnienia roszczeń z umowy.',
    recipients: 'Dostawca hostingu sklepu, system obsługi zamówień, operator płatności, przewoźnicy i pośrednik wysyłek, dostawca wysyłający towar bezpośrednio do klienta.',
    processors: ['p-hosting', 'p-baselinker', 'p-platnosci', 'p-vps'],
    jointController: '', systems: 'Sklep WooCommerce, BaseLinker, poczta e-mail, automatyzacje n8n.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'faktury', recommended: true, hint: 'Faktury, ewidencja przychodów i VAT, rozliczenia z urzędem.',
    name: 'Faktury i rozliczenia podatkowe',
    purposes: 'Wystawianie faktur, prowadzenie ewidencji przychodów i ewidencji VAT, rozliczenia podatkowe i przechowywanie dokumentacji.',
    legalBasis: 'Art. 6 ust. 1 lit. c RODO w związku z ustawą o VAT, ustawą o zryczałtowanym podatku dochodowym i Ordynacją podatkową.',
    subjects: 'Klienci żądający faktury, kontrahenci, osoby reprezentujące kontrahentów.',
    dataCategories: 'Imię i nazwisko lub nazwa firmy, adres, NIP, dane z faktury (towary, usługi, kwoty), numer rachunku bankowego.',
    special: false, source: 'Od klienta lub kontrahenta.',
    retention: '5 lat od końca roku kalendarzowego, w którym upłynął termin płatności podatku (art. 70 i 86 Ordynacji podatkowej).',
    recipients: 'Biuro rachunkowe, system do fakturowania, organy podatkowe (w tym KSeF).',
    processors: ['p-infakt', 'p-biuro', 'p-baselinker'],
    jointController: '', systems: 'inFakt, BaseLinker, KSeF.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'reklamacje', recommended: true, hint: 'Reklamacje, zwroty, odstąpienie od umowy.',
    name: 'Reklamacje, zwroty i odstąpienie od umowy',
    purposes: 'Rozpatrywanie reklamacji, obsługa zwrotów i odstąpienia od umowy, zwrot płatności.',
    legalBasis: 'Art. 6 ust. 1 lit. c RODO w związku z ustawą o prawach konsumenta i Kodeksem cywilnym; art. 6 ust. 1 lit. b RODO.',
    subjects: 'Klienci zgłaszający reklamację lub zwrot.',
    dataCategories: 'Imię i nazwisko, dane kontaktowe, adres, numer zamówienia, opis i zdjęcia wady, numer rachunku bankowego do zwrotu.',
    special: false, source: 'Bezpośrednio od klienta.',
    retention: 'Przez czas rozpatrywania sprawy, a następnie do upływu terminu przedawnienia roszczeń.',
    recipients: 'Przewoźnicy (odbiór zwrotu), biuro rachunkowe (korekty), dostawca poczty e-mail.',
    processors: ['p-hosting', 'p-biuro'],
    jointController: '', systems: 'Poczta e-mail, BaseLinker.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'kontakt', recommended: true, hint: 'Odpowiedzi na pytania przez e-mail, Instagram i Messenger.',
    name: 'Korespondencja z klientami',
    purposes: 'Odpowiadanie na pytania przesłane e-mailem i przez media społecznościowe (Instagram, Messenger).',
    legalBasis: 'Art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes: odpowiedź na wiadomość); art. 6 ust. 1 lit. b, gdy wiadomość dotyczy zamówienia.',
    subjects: 'Osoby kontaktujące się z firmą.',
    dataCategories: 'Imię i nazwisko lub nazwa profilu, adres e-mail, treść korespondencji.',
    special: false, source: 'Bezpośrednio od osoby piszącej.',
    retention: 'Przez czas potrzebny do obsługi sprawy, następnie do roku, chyba że korespondencja dotyczy zamówienia lub reklamacji.',
    recipients: 'Dostawca poczty e-mail, Meta Platforms Ireland (Instagram, Messenger) jako odrębny administrator.',
    processors: ['p-hosting', 'p-google'],
    jointController: '', systems: 'Poczta e-mail, Instagram, Messenger.',
    security: '', transfers: 'Możliwy transfer do USA przez dostawców platform społecznościowych.',
    transferSafeguards: 'Decyzja Komisji Europejskiej w sprawie EU–US Data Privacy Framework albo standardowe klauzule umowne dostawcy.',
    dpia: 'Nie wymagana.',
  },
  {
    key: 'opinie', recommended: true, hint: 'Opinie o produktach pokazywane pod produktami.',
    name: 'Opinie o produktach',
    purposes: 'Publikowanie opinii klientów o produktach w sklepie.',
    legalBasis: 'Art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes: prezentowanie opinii o produktach).',
    subjects: 'Klienci i użytkownicy dodający opinie.',
    dataCategories: 'Imię lub pseudonim, adres e-mail (niepublikowany), treść opinii, ocena, data.',
    special: false, source: 'Bezpośrednio od autora opinii.',
    retention: 'Do usunięcia opinii, zgłoszenia sprzeciwu albo zamknięcia sklepu.',
    recipients: 'Dostawca hostingu sklepu; treść opinii jest publiczna.',
    processors: ['p-hosting'],
    jointController: '', systems: 'Sklep WooCommerce.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'analityka', recommended: true, hint: 'Google Analytics, tag Google Ads i Meta Pixel po zgodzie w banerze cookies.',
    name: 'Analityka i reklama (pliki cookies)',
    purposes: 'Statystyki ruchu w sklepie, mierzenie skuteczności reklam i remarketing.',
    legalBasis: 'Art. 6 ust. 1 lit. a RODO (zgoda w banerze cookies) w związku z art. 399 Prawa komunikacji elektronicznej.',
    subjects: 'Odwiedzający sklep, którzy wyrazili zgodę.',
    dataCategories: 'Identyfikatory plików cookies, adres IP, informacje o urządzeniu i przeglądarce, aktywność w sklepie (odsłony, dodania do koszyka, zakupy).',
    special: false, source: 'Pliki cookies i podobne technologie w przeglądarce odwiedzającego.',
    retention: 'Zgodnie z ustawieniami narzędzi (np. czas przechowywania danych w Google Analytics) i do wycofania zgody.',
    recipients: 'Google Ireland (Analytics, Ads), Meta Platforms Ireland (Pixel).',
    processors: ['p-hosting'],
    jointController: 'Meta Platforms Ireland Ltd. – w zakresie zbierania i przekazywania danych przez Meta Pixel (wyrok TSUE C-40/17 Fashion ID).',
    systems: 'Google Analytics, Google Ads, Meta Pixel, baner zgód Elementor Cookie Consent.',
    security: '', transfers: 'USA (Google LLC, Meta Platforms Inc.).',
    transferSafeguards: 'Decyzja Komisji Europejskiej w sprawie EU–US Data Privacy Framework (certyfikacja odbiorców) oraz standardowe klauzule umowne.',
    dpia: 'Nie wymagana; sprawdź ponownie przy profilowaniu na dużą skalę.',
  },
  {
    key: 'b2b', recommended: true, hint: 'Klienci usług i dostawcy: umowy i osoby kontaktowe.',
    name: 'Współpraca z kontrahentami',
    purposes: 'Zawarcie i wykonanie umów z klientami usług i dostawcami, kontakt z ich przedstawicielami.',
    legalBasis: 'Art. 6 ust. 1 lit. b RODO (umowa z kontrahentem będącym osobą fizyczną); art. 6 ust. 1 lit. f RODO (kontakt z osobami reprezentującymi kontrahenta).',
    subjects: 'Kontrahenci prowadzący działalność, osoby kontaktowe kontrahentów.',
    dataCategories: 'Imię i nazwisko, stanowisko, służbowy e-mail i telefon, dane firmy (NIP, adres).',
    special: false, source: 'Od kontrahenta lub z publicznych rejestrów (CEIDG, KRS).',
    retention: 'Przez czas współpracy, a następnie do upływu terminu przedawnienia roszczeń.',
    recipients: 'Biuro rachunkowe, system do fakturowania, dostawca poczty i dokumentów.',
    processors: ['p-infakt', 'p-biuro', 'p-google'],
    jointController: '', systems: 'inFakt, Google Workspace.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'roszczenia', recommended: true, hint: 'Dochodzenie i obrona przed roszczeniami.',
    name: 'Ustalanie, dochodzenie i obrona roszczeń',
    purposes: 'Ustalenie, dochodzenie lub obrona przed roszczeniami związanymi ze sprzedażą i usługami.',
    legalBasis: 'Art. 6 ust. 1 lit. f RODO (prawnie uzasadniony interes: ochrona przed roszczeniami).',
    subjects: 'Klienci, kontrahenci.',
    dataCategories: 'Dane z umów, zamówień, faktur i korespondencji.',
    special: false, source: 'Z dokumentacji firmy.',
    retention: 'Do upływu terminu przedawnienia roszczeń, a w razie sporu do jego prawomocnego zakończenia.',
    recipients: 'Kancelarie prawne, sądy i komornicy (na podstawie przepisów).',
    processors: ['p-google'],
    jointController: '', systems: 'Google Workspace.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'newsletter', recommended: false, hint: 'Tylko jeśli wysyłasz newsletter.',
    name: 'Newsletter', purposes: 'Wysyłka informacji handlowych e-mailem.',
    legalBasis: 'Art. 6 ust. 1 lit. a RODO (zgoda) w związku z art. 398 Prawa komunikacji elektronicznej.',
    subjects: 'Subskrybenci newslettera.', dataCategories: 'Adres e-mail, imię, historia otwarć i kliknięć.',
    special: false, source: 'Bezpośrednio od subskrybenta.', retention: 'Do wycofania zgody.',
    recipients: 'Dostawca systemu mailingowego.', processors: [], jointController: '', systems: '',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'konta', recommended: false, hint: 'Tylko jeśli klienci zakładają konta w sklepie.',
    name: 'Konta klientów w sklepie', purposes: 'Prowadzenie konta klienta w sklepie.',
    legalBasis: 'Art. 6 ust. 1 lit. b RODO (umowa o świadczenie usługi konta).',
    subjects: 'Klienci z kontem.', dataCategories: 'Imię i nazwisko, e-mail, hasło (w postaci skrótu), adresy, historia zamówień.',
    special: false, source: 'Bezpośrednio od klienta.', retention: 'Do usunięcia konta.',
    recipients: 'Dostawca hostingu sklepu.', processors: ['p-hosting'], jointController: '', systems: 'Sklep WooCommerce.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'kadry', recommended: false, hint: 'Tylko jeśli zatrudniasz lub rekrutujesz.',
    name: 'Zatrudnienie i rekrutacja', purposes: 'Rekrutacja, zatrudnienie, rozliczenia wynagrodzeń, ZUS i podatków.',
    legalBasis: 'Art. 6 ust. 1 lit. b i c RODO w związku z Kodeksem pracy; art. 9 ust. 2 lit. b RODO (dane o zdrowiu).',
    subjects: 'Kandydaci, pracownicy, zleceniobiorcy.', dataCategories: 'Dane z art. 22¹ Kodeksu pracy, dane do rozliczeń, zaświadczenia lekarskie.',
    special: true, source: 'Bezpośrednio od kandydata lub pracownika.', retention: 'Dokumentacja pracownicza: 10 lat od końca roku ustania zatrudnienia.',
    recipients: 'Biuro rachunkowe, ZUS, urząd skarbowy.', processors: ['p-biuro'], jointController: '', systems: '',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Nie wymagana.',
  },
  {
    key: 'powierzenie', role: 'processor', recommended: false, hint: 'Gdy w ramach usług masz dostęp do danych osobowych klientów swoich klientów.',
    name: 'Usługi dla klientów z dostępem do ich danych', onBehalfOf: 'Klienci usług (wpisz nazwy)',
    purposes: 'Prace programistyczne i utrzymaniowe w systemach klienta, w trakcie których możliwy jest dostęp do danych osobowych.',
    legalBasis: 'Umowa powierzenia przetwarzania danych z klientem (art. 28 RODO).',
    subjects: 'Użytkownicy i klienci systemów klienta.', dataCategories: 'Zgodnie z umową powierzenia (np. dane kontaktowe i identyfikacyjne użytkowników).',
    special: false, source: 'Systemy klienta.', retention: 'Do zakończenia umowy, następnie usunięcie lub zwrot danych zgodnie z umową.',
    recipients: 'Wyłącznie klient (administrator).', processors: [], jointController: '', systems: 'Systemy klienta, repozytoria kodu.',
    security: '', transfers: '', transferSafeguards: '', dpia: 'Po stronie administratora.',
  },
];

/** Typowe podmioty przetwarzające dla tej działalności. Status umów do potwierdzenia. */
export const RODO_PROCESSOR_PRESETS: Record<string, RodoProcessor> = {
  'p-hosting': { name: 'cyber_Folks', service: 'Hosting sklepu i poczty e-mail', country: 'PL', dpa: 'unknown' },
  'p-baselinker': { name: 'BaseLinker', service: 'Obsługa zamówień i wysyłek, faktury', country: 'PL', dpa: 'unknown' },
  'p-infakt': { name: 'inFakt', service: 'Fakturowanie', country: 'PL', dpa: 'unknown' },
  'p-biuro': { name: 'Biuro rachunkowe', service: 'Księgowość i rozliczenia', country: 'PL', dpa: 'unknown' },
  'p-vps': { name: 'Hostinger', service: 'Serwer VPS (automatyzacje n8n, aplikacja Biuro)', country: 'UE', dpa: 'unknown' },
  'p-google': { name: 'Google Workspace', service: 'Poczta, Dysk, Kalendarz', country: 'IE (transfer do USA: EU–US DPF)', dpa: 'unknown' },
  'p-platnosci': { name: 'Operator płatności', service: 'Płatności online w sklepie (wpisz nazwę)', country: 'PL', dpa: 'unknown' },
};

export function activityFromTemplate(t: Tpl, order: number): RodoActivity {
  const { key, hint: _h, recommended: _r, processors, ...rest } = t;
  return { ...rest, role: t.role || 'controller', processorIds: processors, active: true, order, templateKey: key };
}

// ---------- dokument ----------
const h = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const br = (s: string) => h(s).replace(/\n/g, '<br>');

export interface RodoBundle {
  main: RodoMain;
  activities: [string, RodoActivity][];
  processors: Record<string, RodoProcessor>;
}

/** Rejestr jako samodzielny dokument HTML (wydruk, PDF, Dokumenty Google). Pola jak we wzorze UODO. */
export function renderRodoHtml({ main, activities, processors }: RodoBundle): string {
  const procName = (id: string) => processors[id] ? `${processors[id].name} – ${processors[id].service}` : '';
  const rows = (a: RodoActivity): [string, string][] => [
    ['Cel przetwarzania', a.purposes],
    ['Podstawa prawna', a.legalBasis],
    ['Kategorie osób', a.subjects],
    ['Kategorie danych', a.dataCategories + (a.special ? '\nZawiera dane szczególnych kategorii (art. 9 lub 10 RODO).' : '')],
    ['Źródło danych', a.source],
    ['Planowany termin usunięcia', a.retention],
    ['Współadministrator', a.jointController || 'Nie dotyczy.'],
    ['Podmioty przetwarzające', a.processorIds.map(procName).filter(Boolean).join('\n') || 'Brak.'],
    ['Kategorie odbiorców', a.recipients],
    ['Systemy i aplikacje', a.systems || '—'],
    ['Środki bezpieczeństwa', a.security || 'Zgodnie z opisem ogólnym środków bezpieczeństwa w nagłówku rejestru.'],
    ['Przekazanie do państwa trzeciego', a.transfers || 'Nie dotyczy.'],
    ['Zabezpieczenia przekazania', a.transfers ? a.transferSafeguards || '—' : 'Nie dotyczy.'],
    ['Ocena skutków (DPIA)', a.dpia || '—'],
  ];
  const table = (r: [string, string][]) =>
    `<table><tbody>${r.map(([k, v]) => `<tr><th>${h(k)}</th><td>${br(v)}</td></tr>`).join('')}</tbody></table>`;
  const ctrl = activities.filter(([, a]) => a.role === 'controller' && a.active);
  const proc = activities.filter(([, a]) => a.role === 'processor' && a.active);
  const usedProc = Object.entries(processors);
  return `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>Rejestr czynności przetwarzania – ${h(main.controllerName)}</title>
<style>
body{font-family:Arial,Helvetica,sans-serif;font-size:10.5pt;color:#111;margin:24px;line-height:1.4}
h1{font-size:18pt;margin:0 0 4px}h2{font-size:13pt;margin:26px 0 8px;border-bottom:1px solid #999;padding-bottom:3px}h3{font-size:11.5pt;margin:18px 0 6px}
table{border-collapse:collapse;width:100%;margin:0 0 10px}th,td{border:1px solid #999;padding:5px 7px;vertical-align:top;text-align:left}
th{background:#f0f0f0;width:30%;font-weight:bold}.meta td,.meta th{border:0;padding:2px 8px 2px 0;background:none}.small{font-size:9pt;color:#444}
.proc th{width:auto}@media print{h3{break-after:avoid}table{break-inside:auto}tr{break-inside:avoid}}
</style></head><body>
<h1>Rejestr czynności przetwarzania</h1>
<p class="small">Prowadzony na podstawie art. 30 ust. 1 RODO${proc.length ? ' wraz z rejestrem kategorii czynności przetwarzania (art. 30 ust. 2 RODO)' : ''}. Stan na ${h(fmtD(main.asOf))}${main.reviewedAt ? `, ostatni przegląd ${h(fmtD(main.reviewedAt))}` : ''}.</p>
<table class="meta"><tbody>
<tr><th>Administrator</th><td>${h(main.controllerName)}</td></tr>
<tr><th>Adres</th><td>${h(main.address)}</td></tr>
<tr><th>NIP / REGON</th><td>${h(main.nip)}${main.regon ? ' / ' + h(main.regon) : ''}</td></tr>
<tr><th>Kontakt</th><td>${h([main.email, main.phone].filter(Boolean).join(', '))}</td></tr>
${main.representative ? `<tr><th>Przedstawiciel</th><td>${h(main.representative)}</td></tr>` : ''}
<tr><th>Inspektor ochrony danych</th><td>${h(main.dpo || 'Nie wyznaczono.')}</td></tr>
</tbody></table>
<h2>Ogólny opis środków bezpieczeństwa (art. 32 ust. 1 RODO)</h2>
<p>${br(main.security)}</p>
<h2>Czynności przetwarzania – administrator</h2>
${ctrl.map(([, a], i) => `<h3>${i + 1}. ${h(a.name)}</h3>${table(rows(a))}`).join('') || '<p>Brak.</p>'}
${proc.length ? `<h2>Kategorie czynności przetwarzania – podmiot przetwarzający</h2>
${proc.map(([, a], i) => `<h3>${i + 1}. ${h(a.name)}</h3>${table([
    ['Administrator, w imieniu którego przetwarzam', a.onBehalfOf || '—'],
    ['Kategorie przetwarzania', a.purposes],
    ['Kategorie danych i osób', `${a.dataCategories}\n${a.subjects}`],
    ['Przekazanie do państwa trzeciego', a.transfers || 'Nie dotyczy.'],
    ['Środki bezpieczeństwa', a.security || 'Zgodnie z opisem ogólnym środków bezpieczeństwa.'],
  ])}`).join('')}` : ''}
<h2>Podmioty przetwarzające</h2>
${usedProc.length ? `<table class="proc"><thead><tr><th>Podmiot</th><th>Zakres usługi</th><th>Kraj</th><th>Umowa powierzenia</th></tr></thead><tbody>
${usedProc.map(([, p]) => `<tr><td>${h(p.name)}${p.contact ? `<br><span class="small">${h(p.contact)}</span>` : ''}</td><td>${h(p.service)}</td><td>${h(p.country)}</td><td>${h(DPA_LABEL[p.dpa])}${p.notes ? `<br><span class="small">${h(p.notes)}</span>` : ''}</td></tr>`).join('')}
</tbody></table>` : '<p>Brak.</p>'}
</body></html>`;
}

/** CSV z kolumnami wzoru UODO (średniki, BOM – dla polskiego Excela). */
export function rodoCSV({ activities, processors }: RodoBundle): string {
  const q = (s: unknown) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const H = ['Lp.', 'Rola', 'Nazwa czynności', 'Cel', 'Podstawa prawna', 'Kategorie osób', 'Kategorie danych', 'Dane szczególne', 'Źródło danych',
    'Termin usunięcia', 'Współadministrator', 'Podmioty przetwarzające', 'Kategorie odbiorców', 'Systemy', 'Środki bezpieczeństwa',
    'Przekazanie do państwa trzeciego', 'Zabezpieczenia przekazania', 'DPIA'];
  const lines = [H.map(q).join(';')];
  activities.filter(([, a]) => a.active).forEach(([, a], i) => {
    lines.push([i + 1, a.role === 'processor' ? `podmiot przetwarzający (${a.onBehalfOf || ''})` : 'administrator', a.name, a.purposes, a.legalBasis,
      a.subjects, a.dataCategories, a.special ? 'tak' : 'nie', a.source, a.retention, a.jointController,
      a.processorIds.map((id) => processors[id]?.name).filter(Boolean).join(', '), a.recipients, a.systems, a.security || 'zgodnie z opisem ogólnym',
      a.transfers, a.transferSafeguards, a.dpia].map(q).join(';'));
  });
  return '﻿' + lines.join('\r\n');
}

/** Rzeczy do sprawdzenia w rejestrze. */
export function rodoChecks(b: RodoBundle, today: Date): string[] {
  const out: string[] = [];
  const used = new Set(b.activities.filter(([, a]) => a.active).flatMap(([, a]) => a.processorIds));
  for (const id of used) {
    const p = b.processors[id];
    if (!p) continue;
    if (p.dpa === 'unknown') out.push(`${p.name}: sprawdź, czy masz umowę powierzenia (zwykle jest w regulaminie lub panelu usługi).`);
    if (p.dpa === 'none') out.push(`${p.name}: brak umowy powierzenia – zawrzyj ją albo zaakceptuj w panelu dostawcy.`);
    if (/wpisz/i.test(p.service + p.name)) out.push(`${p.name}: uzupełnij nazwę dostawcy.`);
  }
  for (const [, a] of b.activities) {
    if (!a.active) continue;
    if (a.transfers && !a.transferSafeguards) out.push(`${a.name}: opisz zabezpieczenia przekazania danych poza EOG.`);
    if (a.role === 'processor' && /wpisz/i.test(a.onBehalfOf || '')) out.push(`${a.name}: wpisz klientów, w imieniu których przetwarzasz dane.`);
  }
  const last = b.main.reviewedAt || b.main.asOf;
  if (last && (today.getTime() - new Date(last).getTime()) / 864e5 > 365) out.push('Rejestr nie był przeglądany od ponad roku.');
  return out;
}
